import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'
import i18n from '@/utils/i18n'

/**
 * Документы компании (подписанные соглашения, доверенности, прочее).
 *
 * Загрузка — только админами. Удаление через клиентский API запрещено:
 * RLS на `public.company_documents` и на bucket'е `company-documents`
 * не выдаёт ни одной роли политику DELETE/UPDATE.
 *
 * См. миграцию `20260526120000_company_documents.sql` в репо `stryzh`.
 */

export interface CompanyDocument {
  id: string
  company_id: string
  file_path: string
  file_name: string
  file_size: number
  mime_type: string | null
  description: string
  uploaded_at: string
  uploaded_by: string | null
  uploaded_by_email?: string | null
}

const BUCKET = 'company-documents'

/** 25 МБ — должно совпадать с CHECK file_size и file_size_limit bucket'а. */
export const COMPANY_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024

// ─── мок-стор (только при USE_MOCK=true) ────────────────────────────────────
const mockDocs: CompanyDocument[] = []

export async function listCompanyDocuments(
  companyId: string,
): Promise<CompanyDocument[]> {
  if (USE_MOCK) {
    return mockDocs
      .filter((d) => d.company_id === companyId)
      .sort((a, b) => b.uploaded_at.localeCompare(a.uploaded_at))
  }

  const supabase = createClient()
  const { data, error } = await supabase
    .from('company_documents')
    .select('*')
    .eq('company_id', companyId)
    .order('uploaded_at', { ascending: false })

  if (error) throw new Error(error.message)
  const docs = (data ?? []) as CompanyDocument[]

  // Подтягиваем подпись загрузившего отдельным запросом. profiles.id == auth.users.id.
  const uploaderIds = Array.from(
    new Set(docs.map((d) => d.uploaded_by).filter((x): x is string => !!x)),
  )
  if (uploaderIds.length === 0) return docs

  const { data: profilesData } = await supabase
    .from('profiles')
    .select('id, email, full_name')
    .in('id', uploaderIds)

  const byId = new Map<string, { email: string | null; full_name: string | null }>()
  for (const p of (profilesData ?? []) as Array<{
    id: string
    email: string | null
    full_name: string | null
  }>) {
    byId.set(p.id, { email: p.email, full_name: p.full_name })
  }
  return docs.map((d) => ({
    ...d,
    uploaded_by_email:
      d.uploaded_by
        ? byId.get(d.uploaded_by)?.full_name ||
          byId.get(d.uploaded_by)?.email ||
          null
        : null,
  }))
}

/**
 * Загружает файл и создаёт запись в `company_documents`.
 * Если запись в БД не удалась — файл удаляется политики DELETE нет
 * (только service_role), поэтому останется «осиротевшим». Это допустимо.
 */
export async function uploadCompanyDocument(
  companyId: string,
  file: File,
  description: string,
): Promise<CompanyDocument> {
  if (file.size <= 0) throw new Error(i18n.t('apiErr.docs.emptyFile'))
  if (file.size > COMPANY_DOCUMENT_MAX_BYTES) {
    throw new Error(i18n.t('apiErr.docs.fileTooLarge'))
  }
  const desc = description.trim()
  if (!desc) throw new Error(i18n.t('apiErr.docs.descriptionRequired'))

  if (USE_MOCK) {
    const now = new Date().toISOString()
    const doc: CompanyDocument = {
      id: `doc-${crypto.randomUUID().slice(0, 8)}`,
      company_id: companyId,
      file_path: `${companyId}/${crypto.randomUUID()}`,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || null,
      description: desc,
      uploaded_at: now,
      uploaded_by: 'mock-admin',
      uploaded_by_email: 'admin@stryzh.ru',
    }
    mockDocs.push(doc)
    return doc
  }

  const supabase = createClient()
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData?.user?.id
  if (!userId) throw new Error(i18n.t('apiErr.docs.sessionExpired'))

  const extMatch = file.name.match(/\.([^.]+)$/)
  const ext = (extMatch?.[1] ?? '').toLowerCase().replace(/[^a-z0-9]/g, '')
  const fileId = crypto.randomUUID()
  const path = ext ? `${companyId}/${fileId}.${ext}` : `${companyId}/${fileId}`

  const { error: upErr } = await supabase.storage.from(BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || 'application/octet-stream',
    cacheControl: '0',
  })
  if (upErr) throw new Error(upErr.message)

  const { data: inserted, error: insErr } = await supabase
    .from('company_documents')
    .insert({
      company_id: companyId,
      file_path: path,
      file_name: file.name,
      file_size: file.size,
      mime_type: file.type || null,
      description: desc,
      uploaded_by: userId,
    })
    .select()
    .single()

  if (insErr) throw new Error(insErr.message)
  return inserted as CompanyDocument
}

/** Signed URL на 5 минут — bucket приватный. */
export async function getCompanyDocumentSignedUrl(
  path: string,
  expiresInSec = 300,
): Promise<string | null> {
  if (USE_MOCK) return path || null
  if (!path) return null
  const supabase = createClient()
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, expiresInSec)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}
